import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { getStatusSummary } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { Sheet, Input, nombreRel } from "@/pages/Familia";
import { formatApiErrorDetail } from "@/lib/api";
import { ConAvatar } from "@/components/personas/Avatar";

const MiniCounter = ({ color, value, label }) => (
  <div className="flex-1 text-center">
    <p className="font-heading text-xl font-extrabold" style={{ color }}>
      {value}
    </p>
    <p className="text-xs font-semibold text-[#6E7A74]">{label}</p>
  </div>
);

export default function Mascotas() {
  const navigate = useNavigate();
  const { pets: PETS, familyMembers, getMattersByPet, getMemberById, createMascota } = useData();
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const empty = { name: "", species: "Perro", breed: "", birth_date: "", microchip: "", insurer: "", vet: "", notes: "", owner_member_id: familyMembers[0]?.id || "fm-self" };

  const save = async () => {
    if (!modal.name.trim()) return toast.error("Indica el nombre de la mascota.");
    setBusy(true);
    try {
      const sp = modal.species.toLowerCase();
      const icon = sp.includes("gato") ? "Cat" : sp.includes("perro") ? "Dog" : "PawPrint";
      await createMascota({ ...modal, name: modal.name.trim(), icon });
      toast.success("Mascota añadida");
      setModal(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <button
        data-testid="mascotas-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]"
      >
        <Icons.ChevronLeft size={22} /> Volver
      </button>

      <header className="aldia-fade-up flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
          <Icons.PawPrint size={26} className="text-[#3A6B56]" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">
            Mascotas
          </h1>
          <p className="text-base text-[#6E7A74]">
            Las mascotas de tu núcleo familiar y sus asuntos.
          </p>
        </div>
      </header>

      <button
        data-testid="mascota-add"
        onClick={() => setModal(empty)}
        className="w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2"
      >
        <Icons.Plus size={24} /> Añadir mascota
      </button>

      <div className="space-y-4" data-testid="mascotas-list">
        {PETS.map((pet, idx) => {
          const Icon = Icons[pet.icon] || Icons.PawPrint;
          const s = getStatusSummary(getMattersByPet(pet.id));
          const owner = getMemberById(pet.owner_member_id);
          return (
            <button
              key={pet.id}
              data-testid={`mascota-card-${pet.id}`}
              onClick={() => navigate(`/mascota/${pet.id}`)}
              className="aldia-fade-up w-full text-left bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-[#3A6B56]/50 transition-all"
              style={{ animationDelay: `${idx * 70}ms` }}
            >
              <div className="flex items-center gap-4">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center">
                  <Icon size={30} className="text-[#3A6B56]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-xl font-bold text-[#1C2421]">{pet.name}</h3>
                  <p className="text-sm text-[#6E7A74]">
                    {pet.species} · {pet.breed} · {pet.age_label}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1 mt-1.5">
                    <Icons.User size={13} /> {nombreRel(owner)}
                  </span>
                </div>
                <Icons.ChevronRight size={22} className="text-[#C4BDB0] shrink-0" />
              </div>
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#E5DFD5]/70">
                <MiniCounter color="#DC2626" value={s.attention} label="Requiere atención" />
                <div className="w-px h-8 bg-[#E5DFD5]" />
                <MiniCounter color="#D97706" value={s.upcoming} label="Próximamente" />
                <div className="w-px h-8 bg-[#E5DFD5]" />
                <MiniCounter color="#16A34A" value={s.controlled} label="Documentación al día" />
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-start gap-2 bg-[#F6F3EE] rounded-xl p-4">
        <Icons.Info size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
        <p className="text-sm text-[#6E7A74] leading-relaxed">
          Las mascotas pertenecen a tu núcleo familiar. Sus citas y asuntos aparecen
          también en Inicio y en Próximos, como cualquier otro asunto.
        </p>
      </div>

      {modal && (
        <Sheet onClose={() => setModal(null)} testid="mascota-form">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">Añadir mascota</h2>
          <Input label="Nombre" value={modal.name} onChange={(v) => setModal({ ...modal, name: v })} testid="mascota-name" placeholder="Ej. Luna" />
          <Input label="Especie" value={modal.species} onChange={(v) => setModal({ ...modal, species: v })} testid="mascota-species" placeholder="Perro, Gato…" />
          <Input label="Raza" value={modal.breed} onChange={(v) => setModal({ ...modal, breed: v })} testid="mascota-breed" placeholder="Ej. Labrador" />
          <Input label="Fecha de nacimiento" type="date" value={modal.birth_date} onChange={(v) => setModal({ ...modal, birth_date: v })} testid="mascota-birth" />
          <Input label="Nº de microchip" value={modal.microchip} onChange={(v) => setModal({ ...modal, microchip: v })} testid="mascota-chip" placeholder="Opcional" />
          <Input label="Seguro / aseguradora" value={modal.insurer} onChange={(v) => setModal({ ...modal, insurer: v })} testid="mascota-insurer" placeholder="Opcional" />
          <Input label="Veterinario habitual" value={modal.vet} onChange={(v) => setModal({ ...modal, vet: v })} testid="mascota-vet" placeholder="Opcional" />
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Propietario (familia)</label>
            <ConAvatar id={modal.owner_member_id}><select
              data-testid="mascota-owner"
              value={modal.owner_member_id}
              onChange={(e) => setModal({ ...modal, owner_member_id: e.target.value })}
              className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]"
            >
              {familyMembers.map((f) => (
                <option key={f.id} value={f.id}>{nombreRel(f)}</option>
              ))}
            </select></ConAvatar>
          </div>
          <button data-testid="mascota-save" onClick={save} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60">{busy ? "Guardando…" : "Guardar mascota"}</button>
        </Sheet>
      )}
    </div>
  );
}
